import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import PublicHeader from '../components/Layout/PublicHeader';
import StaticPageTopbar from '../components/Layout/StaticPageTopbar';
import organizerAccessService from '../services/organizerAccessService';

// ── „Für euch wurde ein Zugang beantragt" ───────────────────────────────────────────────────
// Die Seite hinter dem Freischalt-Link, den der Verein bekommt (V160). Ohne Konto: die
// Berechtigung ist der Zugriff auf das Vereinspostfach, genau wie beim Einmal-Link aus der
// Outreach-Mail.
//
// Die Entscheidung fällt im Postfach, nicht hier: wer in der Mail auf „Zugang bestätigen" klickt,
// hat entschieden. Die Seite schaltet deshalb beim Aufruf frei und sagt nur noch, was passiert
// ist. Ein zweiter Knopf hätte dieselbe Frage ein zweites Mal gestellt.
//
// Sie tut es per POST aus dem JavaScript heraus und nicht über die Adresse selbst: Mail-Scanner
// rufen Links aus Mails ungefragt ab, führen aber kein JavaScript aus und schicken kein POST.
// Ein GET mit dieser Wirkung wäre von einem Scanner auslösbar, und der Verein hätte nie geklickt.

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const INK = '#2C2C2C';
const MUTED = '#6A6A6A';
const BORDER = '#E5E1E1';
const BERRY = '#922C57';

const card: React.CSSProperties = {
  background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 16, padding: 26,
};
const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: FONT_BODY, fontWeight: 700,
  fontSize: 15, padding: '13px 22px', borderRadius: 11, textDecoration: 'none',
  border: `1px solid ${BORDER}`, background: '#fff', color: INK, cursor: 'pointer',
};
const btnPrimary: React.CSSProperties = { ...btn, background: BERRY, borderColor: BERRY, color: '#fff' };
const title: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 24, letterSpacing: '-0.015em',
  color: INK, margin: '10px 0 10px',
};
const text: React.CSSProperties = {
  fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.6, color: MUTED, margin: '0 0 18px',
};

export interface ApprovalInfo {
  organizerName: string;
  requesterName: string;
  /** `PENDING`, `DONE` (schon freigeschaltet) oder `GONE` (abgelaufen oder benutzt). */
  state: string;
}

/**
 * Der Inhalt der Seite — von außen gefüttert, damit das Design-Lab alle Ausgänge nebeneinander
 * zeigt. Drei Zustände: läuft, freigeschaltet, Link gilt nicht mehr.
 */
export const ApprovalView: React.FC<{
  info: ApprovalInfo | null;
  loading?: boolean;
  /** Der Link ist verbraucht, abgelaufen oder erfunden — für den Leser dasselbe. */
  gone?: boolean;
}> = ({ info, loading, gone }) => {
  if (loading) {
    return (
      <div style={card}>
        <p style={{ ...text, margin: 0 }}>Einen Moment, wir schalten den Zugang frei…</p>
      </div>
    );
  }

  if (gone || !info) {
    return (
      <div style={card}>
        <h1 style={title}>Dieser Link gilt nicht mehr.</h1>
        <p style={text}>
          Er wurde schon benutzt, oder er ist abgelaufen. Links gelten 96 Stunden. Wer den Zugang
          wollte, kann ihn einfach noch einmal beantragen. Meldet euch bei uns, wenn etwas nicht
          stimmt.
        </p>
        <Link to="/kontakt" style={btn}>Schreib uns</Link>
      </div>
    );
  }

  return (
    <div style={card}>
      <style>{`.oa-approve-mark { width:52px; height:52px; border-radius:999px; background:#1F8A5B;
          color:#fff; display:flex; align-items:center; justify-content:center;
          animation:oaApprovePop .45s cubic-bezier(.16,1,.3,1) both; }
        @keyframes oaApprovePop { from { transform:scale(.4); opacity:0 } to { transform:scale(1); opacity:1 } }
        @media (prefers-reduced-motion: reduce) { .oa-approve-mark { animation:none } }`}</style>
      <span className="oa-approve-mark" aria-hidden><Check size={26} /></span>
      <h1 style={title}>Zugang freigeschaltet.</h1>
      <p style={text}>
        {info.requesterName} verwaltet jetzt die Seite von {info.organizerName} und kann eure
        Events eintragen und ändern. Ihr müsst nichts weiter tun.
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Link to={'/'} style={btnPrimary}>Zu Radsport Events</Link>
        <Link to="/kontakt" style={btn}>Das war doch niemand von uns</Link>
      </div>
    </div>
  );
};

const OrganizerAccessApprove: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [info, setInfo] = useState<ApprovalInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    if (!token) { setGone(true); setLoading(false); return; }
    let alive = true;
    // Einlösen, nicht fragen: geklickt wurde schon, und zwar im Postfach des Vereins.
    organizerAccessService.approveByLink(token)
      .then(result => { if (alive) setInfo(result); })
      .catch(() => { if (alive) setGone(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>Veranstalter-Zugang bestätigen | Radsport Events</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="hide-mobile"><PublicHeader /></div>
      <StaticPageTopbar label="// VERANSTALTER-ZUGANG" />

      <div style={{ flex: 1, width: '100%', boxSizing: 'border-box', padding: '24px 16px 60px', maxWidth: 620, margin: '0 auto' }}>
        <ApprovalView info={info} loading={loading} gone={gone} />
      </div>

    </div>
  );
};

export default OrganizerAccessApprove;
